export const createDecks = ({ helpers, asset }) => {
  const {
    ExternalLink,
    SoftCard,
    ZoomableImage,
    EmojiCard,
    CleanList,
    SectionLabel,
    ConceptMark,
    ScreenshotFrame,
    NumberedList,
    ComparisonTable,
    BigStatement,
    Timeline,
  } = helpers;

  return {
     overview: {
  label: "Overview",
  description: "core pitch",
  slides: [
    {
      title: "",
      density: "medium",
      content: (
        <div className="relative overflow-hidden px-5 py-8 text-center sm:px-8 sm:py-10">
          <div className="mx-auto inline-flex rounded-full border border-red-200 bg-red-50/80 px-7 py-2 text-sm font-black uppercase tracking-[0.32em] text-red-700 shadow-sm sm:text-base">
            The Post-Learning Plateau
          </div>

          <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center">
            <h1 className="mt-12 font-mono text-5xl font-black leading-[0.9] tracking-tight text-gray-950 sm:text-6xl md:text-7xl xl:text-[6rem]">
              You finished
              <br />
              the language course.
              <br />
              Now what?
            </h1>

            <div className="mx-auto mt-8 h-1.5 w-64 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 sm:w-80" />

            <div className="mx-auto mt-16 grid max-w-6xl gap-6 text-left md:grid-cols-3">
              <SoftCard className="min-h-[260px] rounded-[2rem] border-white/70 bg-white/88 p-8 shadow-sm">
                <div className="mb-7 flex h-24 w-24 items-center justify-center rounded-full bg-blue-50">
                  <svg width="54" height="54" viewBox="0 0 54 54" fill="none">
                    <circle cx="27" cy="27" r="21" stroke="#2563eb" strokeWidth="3" />
                    <circle cx="20" cy="23" r="2.8" fill="#2563eb" />
                    <circle cx="34" cy="23" r="2.8" fill="#2563eb" />
                    <path
                      d="M20 34H34"
                      stroke="#2563eb"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                <div className="text-[1.55rem] font-black leading-[1.16] text-gray-900 xl:text-[1.75rem]">
                  You can follow
                  <br />
                  lessons...
                  <br />
                  Real speech
                  <br />
                  still feels fragile.
                </div>
              </SoftCard>

              <SoftCard className="min-h-[260px] rounded-[2rem] border-white/70 bg-white/88 p-8 shadow-sm">
                <div className="mb-7 flex h-24 w-24 items-center justify-center rounded-full bg-violet-50">
                  <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                    <path
                      d="M15 46V22L28 12L41 22V46"
                      stroke="#7c3aed"
                      strokeWidth="3"
                      strokeLinejoin="round"
                    />
                    <path d="M23 46V34H33V46" stroke="#7c3aed" strokeWidth="3" />
                    <path
                      d="M21 27H21.01"
                      stroke="#7c3aed"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                    <path
                      d="M35 27H35.01"
                      stroke="#7c3aed"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                <div className="text-[1.55rem] font-black leading-[1.16] text-gray-900 xl:text-[1.75rem]">
                  Courses build
                  <br />
                  the basis.
                  <br />
                  Confidence still
                  <br />
                  needs practice.
                </div>
              </SoftCard>

              <SoftCard className="min-h-[260px] rounded-[2rem] border-white/70 bg-white/88 p-8 shadow-sm">
                <div className="mb-7 flex h-24 w-24 items-center justify-center rounded-full bg-rose-50">
                  <svg width="74" height="54" viewBox="0 0 74 54" fill="none">
                    <path
                      d="M8 46V10"
                      stroke="#9ca3af"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <path
                      d="M8 46H66"
                      stroke="#9ca3af"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <path
                      d="M18 36 L34 20 L48 20 L62 20"
                      stroke="#f43f5e"
                      strokeWidth="4.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="34" cy="20" r="4.5" fill="#f43f5e" />
                    <circle cx="48" cy="20" r="4.5" fill="#f43f5e" />
                  </svg>
                </div>

                <div className="text-[1.55rem] font-black leading-[1.16] text-gray-900 xl:text-[1.75rem]">
                  You reached
                  <br />
                  a plateau.
                  <br />
                  What comes
                  <br />
                  next?
                </div>
              </SoftCard>
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),radial-gradient(circle_at_bottom_right,_#fce7f3,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "dense",
      content: (
        <div className="relative overflow-hidden px-4 py-4 text-center sm:px-8 sm:py-5">
          <div className="mx-auto max-w-7xl">
            <h2 className="mx-auto mt-4 max-w-5xl text-4xl font-black leading-[1.02] tracking-tight text-gray-950 sm:text-5xl xl:text-[4rem]">
              People try several ways
              <br />
              <span className="font-medium">after the classroom.</span>
            </h2>

            <div className="mx-auto mt-5 h-1.5 w-52 rounded-full bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 sm:w-64" />

            <div className="mx-auto mt-8 grid w-full max-w-6xl grid-cols-1 gap-5 text-center md:grid-cols-2 xl:grid-cols-4">
              {[
                {
                  icon: "🌍",
                  title: "Immersion",
                  circle: "bg-blue-50",
                  line: "bg-blue-500",
                  text: (
                    <>
                      You communicate,
                      <br />
                      but often with
                      <br />
                      the same safe phrases.
                    </>
                  ),
                  bold: (
                    <>
                      Survival fluency
                      <br />
                      becomes a routine.
                    </>
                  ),
                },
                {
                  icon: "📱",
                  title: "Language apps",
                  circle: "bg-violet-50",
                  line: "bg-violet-500",
                  text: (
                    <>
                      Good for repetition,
                      <br />
                      but weak on real
                      <br />
                      human interaction.
                    </>
                  ),
                  bold: (
                    <>
                      Streaks do not
                      <br />
                      create conversation.
                    </>
                  ),
                },
                {
                  icon: "🧑‍🏫",
                  title: "Private tutors",
                  circle: "bg-amber-50",
                  line: "bg-amber-500",
                  text: (
                    <>
                      One-to-one lessons help,
                      <br />
                      but can feel intense
                      <br />
                      and evaluative.
                    </>
                  ),
                  bold: (
                    <>
                      Speaking needs social ease,
                      <br />
                      not only correction.
                    </>
                  ),
                },
                {
                  icon: "☕",
                  title: "Language cafés",
                  circle: "bg-rose-50",
                  line: "bg-rose-500",
                  text: (
                    <>
                      Friendly and social,
                      <br />
                      but often loose
                      <br />
                      and irregular.
                    </>
                  ),
                  bold: (
                    <>
                      Progress needs continuity
                      <br />
                      and commitment.
                    </>
                  ),
                },
              ].map((item) => (
                <SoftCard
                  key={item.title}
                  className="rounded-[2rem] border-white/70 bg-white/92 px-5 py-6 shadow-sm sm:min-h-[305px]"
                >
                  <div
                    className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${item.circle} text-3xl`}
                  >
                    {item.icon}
                  </div>

                  <h3 className="mt-4 text-[1.45rem] font-black leading-tight text-gray-950 xl:text-[1.6rem]">
                    {item.title}
                  </h3>

                  <div className={`mx-auto mt-3 h-1 w-12 rounded-full ${item.line}`} />

                  <p className="mt-5 text-[1.08rem] font-semibold leading-relaxed text-gray-700 xl:text-[1.13rem]">
                    {item.text}
                  </p>

                  <p className="mt-5 text-[1.08rem] font-black leading-snug text-gray-950 xl:text-[1.13rem]">
                    {item.bold}
                  </p>
                </SoftCard>
              ))}
            </div>

            <div className="mx-auto mt-7 max-w-5xl rounded-[2rem] bg-gradient-to-r from-violet-100 via-pink-100 to-rose-100 px-6 py-5 shadow-sm ring-1 ring-white/70">
              <div className="flex items-center justify-center gap-4 text-center">
                <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/65 text-3xl sm:flex">
                  🧩
                </div>

                <div className="text-2xl font-black leading-tight text-gray-950 md:text-3xl xl:text-[2rem]">
                  The missing piece is{" "}
                  <span className="text-violet-700">
                    structured social speech practice.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),radial-gradient(circle_at_bottom_right,_#fce7f3,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "medium",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-5 text-center sm:px-6">
          <div className="mx-auto max-w-5xl rounded-[2rem] border border-white/70 bg-white/72 px-6 py-5 shadow-sm ring-1 ring-purple-100 sm:px-8">
            <div className="text-sm font-black uppercase tracking-[0.24em] text-purple-700">
              The Catchphraser Approach
            </div>

            <h3 className="mx-auto mt-3 max-w-4xl text-2xl font-black leading-[1.16] text-gray-950 sm:text-3xl xl:text-[2.45rem]">
              Can online practice combine classroom guidance,
              language-café ease, and individual pace?
            </h3>
          </div>

          <div className="mx-auto mt-9 grid w-full max-w-6xl items-stretch gap-6 xl:grid-cols-[0.9fr_1.22fr_0.9fr]">
            <SoftCard className="flex min-h-[320px] flex-col items-center justify-center rounded-[2.2rem] border border-purple-100 bg-white/88 px-8 py-10 text-center shadow-sm">
              <div className="text-6xl leading-none text-purple-300">“</div>

              <div className="mt-3 text-2xl italic leading-snug text-gray-700 sm:text-[2rem]">
                A person needs
                <br />
                another person.
              </div>

              <div className="mt-6 text-[11px] font-black uppercase tracking-[0.24em] text-gray-400">
                Solaris · Tarkovsky
              </div>
            </SoftCard>

            <SoftCard className="relative flex min-h-[390px] flex-col items-center justify-center overflow-hidden rounded-[2.8rem] border border-purple-100 bg-white/95 px-10 py-12 text-center shadow-md ring-1 ring-purple-100">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

              <div className="mb-7 flex justify-center gap-4 text-5xl">
                <span>🧍</span>
                <span>🤝</span>
                <span>👥</span>
                <span>🎭</span>
              </div>

              <div className="text-4xl font-black leading-tight text-gray-950 sm:text-5xl">
                Let us stick
                <span className="text-purple-700"> together.</span>
              </div>

              <div className="mx-auto mt-6 max-w-2xl text-xl font-semibold leading-relaxed text-gray-600">
                Learners need practice that is structured enough to guide them —
                and social enough to make speech feel real.
              </div>
            </SoftCard>

            <SoftCard className="flex min-h-[320px] flex-col items-center justify-center rounded-[2.2rem] border border-blue-100 bg-white/88 px-8 py-10 text-center shadow-sm">
              <div className="text-5xl">💬</div>

              <div className="mt-5 text-3xl font-black leading-tight text-gray-950 sm:text-[2.2rem]">
                Catchphraser adds
                <br />
                <span className="text-blue-700">the social layer.</span>
              </div>

              <div className="mx-auto mt-6 max-w-sm text-lg font-semibold leading-relaxed text-gray-600">
                Practice with feedback,
                peers, and rehearsal —
                before full immersion.
              </div>
            </SoftCard>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "medium",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-6 text-center sm:px-6">
          <div className="text-sm font-black uppercase tracking-[0.24em] text-purple-700">
            Catchphraser baseline
          </div>

          <h3 className="mx-auto mt-4 max-w-5xl text-4xl font-black leading-[1.05] text-gray-950 sm:text-5xl xl:text-[4.2rem]">
            Catchphraser focuses on
            <br />
            pronunciation training.
          </h3>

          <p className="mx-auto mt-7 max-w-3xl text-xl font-bold leading-relaxed text-gray-700 sm:text-2xl">
            Learners practice with human-narrated audio material
            and structured pronunciation courses.
          </p>

          <div className="mx-auto mt-12 grid w-full max-w-5xl gap-6 md:grid-cols-2">
            <SoftCard className="rounded-[2rem] bg-white/92 p-8 text-center shadow-sm ring-1 ring-blue-100">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 text-4xl">
                🎧
              </div>

              <div className="mt-5 text-2xl font-black text-gray-950">
                Human-read material
              </div>

              <p className="mt-4 text-lg font-semibold leading-relaxed text-gray-600">
                Audiobooks, dialogues,
                and authentic narration.
              </p>
            </SoftCard>

            <SoftCard className="rounded-[2rem] bg-white/92 p-8 text-center shadow-sm ring-1 ring-purple-100">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-purple-50 text-4xl">
                📈
              </div>

              <div className="mt-5 text-2xl font-black text-gray-950">
                Rhythm & intonation
              </div>

              <p className="mt-4 text-lg font-semibold leading-relaxed text-gray-600">
                Stress, timing,
                prosody, and speech flow.
              </p>
            </SoftCard>
          </div>

          <div className="mx-auto mt-12 max-w-4xl rounded-[2rem] bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 px-8 py-6 shadow-sm ring-1 ring-white/70">
            <div className="text-2xl font-black leading-tight text-gray-950">
              The aim is not accent elimination.
              <br />
              The aim is intelligible, confident speech
              <br />
              that learners can use in real interaction.
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "medium",
      content: (
        <div className="mx-auto flex min-h-[640px] max-w-7xl flex-col justify-center px-4 py-6 text-center sm:px-6">
          <div className="text-sm font-black uppercase tracking-[0.24em] text-purple-700">
            We use rhythm and flow focused courses:
          </div>

          <h3 className="mx-auto mt-4 max-w-5xl text-4xl font-black leading-[1.05] text-gray-950 sm:text-5xl xl:text-[4rem]">
            Flyt og rytme
          </h3>

          <div className="mx-auto mt-10 grid w-full max-w-6xl items-center gap-7 lg:grid-cols-[0.9fr_1.1fr]">
            <SoftCard className="rounded-[2.3rem] bg-gradient-to-br from-purple-50 to-white p-8 text-center shadow-sm ring-1 ring-purple-100">
              <div className="mx-auto h-40 w-40 overflow-hidden rounded-full border-4 border-white shadow-xl">
                <img
                  src={asset("oh.jpg")}
                  alt="Associate Professor Olaf Husby"
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="mt-6 text-3xl font-black leading-tight text-gray-950">
                Olaf Husby
              </div>

              <div className="mt-3 text-sm font-black uppercase tracking-[0.2em] text-purple-700">
                pronunciation pedagogy
              </div>
            </SoftCard>

            <SoftCard className="rounded-[2.3rem] bg-white/92 p-8 text-left shadow-sm ring-1 ring-blue-100">
              <div className="space-y-6">
                {[
                  [
                    "Spaced repetition loops",
                    "Repetition gives the ear, brain, and speech muscles time to adapt.",
                  ],
                  [
                    "Difficult sounds and combinations",
                    "Feedback helps identify where speech needs repair.",
                  ],
                  [
                    "Toward social practice",
                    "Individual training becomes more workable when it leads into peer feedback and shared rehearsal.",
                  ],
                ].map(([title, text], index) => (
                  <div key={title}>
                    {index > 0 && <div className="mb-6 h-px bg-gray-100" />}

                    <div className="text-2xl font-black text-gray-950">
                      {title}
                    </div>

                    <div className="mt-2 text-lg font-semibold leading-relaxed text-gray-600">
                      {text}
                    </div>
                  </div>
                ))}
              </div>
            </SoftCard>
          </div>

          <div className="mx-auto mt-10 max-w-4xl rounded-[2rem] bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 px-8 py-6 text-2xl font-black leading-tight text-gray-950 shadow-sm ring-1 ring-white/70">
            First: train perception and production.
            <br />
            Then: make practice social.
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#ede9fe,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_32%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "",
      density: "screenshot",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <div className="relative mx-auto w-full max-w-6xl">
            <div className="absolute inset-0 scale-[1.01] rounded-[2.6rem] bg-gradient-to-r from-blue-200/30 via-purple-200/25 to-pink-200/30 blur-3xl" />

            <ZoomableImage
              src={asset("flyt-og-rytme-interface-enhanced.png")}
              alt="Pitch contour visualization showing rhythm, stress, clickable words, and intonation movement"
              caption="Pitch contour visualization"
              className="relative block"
            >
              <div className="relative overflow-hidden rounded-[2.6rem] border border-white/80 bg-white/92 p-3 shadow-[0_30px_95px_rgba(15,23,42,0.15)] ring-1 ring-gray-200/70 transition-all duration-300 group-hover:scale-[1.006]">
                <div className="rounded-[2rem] border border-gray-100 bg-white px-3 py-3">
                  <img
                    src={asset("flyt-og-rytme-interface-enhanced.png")}
                    alt="Pitch contour visualization showing rhythm, stress, clickable words, and intonation movement"
                    className="mx-auto max-h-[455px] w-full object-contain contrast-[1.06] brightness-[1.02]"
                  />
                </div>
              </div>
            </ZoomableImage>
          </div>

          <div className="mx-auto mt-6 grid w-full max-w-6xl gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              "fall / rise",
              "intonation groups",
              <>
                movement inside
                <br />
                words
              </>,
              "clickable words",
            ].map((title, index) => (
              <SoftCard
                key={index}
                className="rounded-[1.7rem] border border-gray-200/80 bg-white/88 px-5 py-5 text-center shadow-sm ring-1 ring-white/80"
              >
                <div className="flex min-h-[56px] items-center justify-center text-2xl font-black leading-tight text-gray-950 xl:text-[1.65rem]">
                  {title}
                </div>
              </SoftCard>
            ))}
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#ede9fe,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "screenshot",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <div className="mx-auto max-w-5xl">
            <div className="text-sm font-black uppercase tracking-[0.24em] text-emerald-700">
              The first social layer
            </div>

            <h3 className="mx-auto mt-3 max-w-4xl text-3xl font-black leading-[1.06] text-gray-950 sm:text-4xl xl:text-[3.35rem]">
              Why leave feedback{" "}
              <br className="hidden sm:block" />
              only to AI?
            </h3>
          </div>

          <div className="relative mx-auto mt-7 w-full max-w-[1220px]">
            <div className="absolute inset-0 scale-[1.01] rounded-[2.4rem] bg-gradient-to-r from-emerald-200/28 via-blue-200/22 to-purple-200/24 blur-3xl" />

            <ZoomableImage
              src={asset("rateme-enhanced.png")}
              alt="Pronunciation review interface where users can rate pronunciation, mark mistakes, and leave comments"
              caption="Pronunciation review interface"
              className="relative block"
            >
              <div className="relative overflow-hidden rounded-[2.4rem] border border-white/80 bg-white/92 p-3 shadow-[0_30px_95px_rgba(15,23,42,0.15)] ring-1 ring-gray-200/70 transition-all duration-300 group-hover:scale-[1.006]">
                <div className="rounded-[1.9rem] border border-gray-100 bg-white px-2 py-2">
                  <img
                    src={asset("rateme-enhanced.png")}
                    alt="Pronunciation review interface where users can rate pronunciation, mark mistakes, and leave comments"
                    className="mx-auto max-h-[470px] w-full object-contain contrast-[1.03] brightness-[1.01]"
                  />
                </div>
              </div>
            </ZoomableImage>
          </div>

          <div className="mx-auto mt-7 max-w-5xl rounded-[2rem] bg-gradient-to-r from-emerald-100 via-blue-100 to-purple-100 px-8 py-6 shadow-sm ring-1 ring-white/80">
            <div className="text-xl font-black leading-relaxed text-gray-950 sm:text-2xl">
              Users, teachers, and native speakers can rate pronunciation
              at phoneme, word, and sentence level,
              and optionally leave practical notes.
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "The First Social Layer: Peer Review",
      density: "medium",
      content: (
        <div className="mx-auto flex max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 sm:text-5xl md:text-6xl">
            Learners gradually learn
            <br />
            how to hear and describe speech.
          </h3>

          <div className="mx-auto mt-6 max-w-4xl">
            <div className="text-lg italic leading-relaxed text-gray-600 sm:text-xl">
              “What a child can do in cooperation today, he will do alone
              tomorrow.”
            </div>

            <div className="mt-2 text-xs font-black uppercase tracking-[0.22em] text-gray-400">
              Vygotsky
            </div>
          </div>

          <div className="mx-auto mt-10 w-full max-w-5xl">
            <div className="rounded-[2rem] border border-emerald-100 bg-white/82 px-5 py-7 shadow-sm ring-1 ring-emerald-100 sm:px-8 sm:py-9">
              <div className="grid items-center gap-7 md:grid-cols-[1fr_140px_1fr]">
                <div className="flex flex-col items-center">
                  <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 text-5xl shadow-sm ring-1 ring-blue-100 sm:h-24 sm:w-24 sm:text-6xl">
                    🧑‍🦱
                  </div>

                  <div className="text-2xl font-black leading-tight text-gray-950 sm:text-3xl">
                    Assess
                    <br />
                    others
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-4xl shadow-sm ring-1 ring-emerald-100 sm:h-24 sm:w-24">
                    💬
                  </div>

                  <div className="mt-4 text-sm font-black uppercase tracking-[0.18em] text-emerald-700">
                    shared terms
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-purple-50 text-5xl shadow-sm ring-1 ring-purple-100 sm:h-24 sm:w-24 sm:text-6xl">
                    👩‍🦰
                  </div>

                  <div className="text-2xl font-black leading-tight text-gray-950 sm:text-3xl">
                    Hear your
                    <br />
                    own mistakes
                  </div>
                </div>
              </div>

              <div className="mx-auto mt-8 max-w-3xl rounded-3xl bg-emerald-50/70 px-6 py-5 text-base font-black leading-relaxed text-gray-950 sm:text-lg">
                Learners develop assessment skill together with practical
                pronunciation terminology.
              </div>
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_32%),radial-gradient(circle_at_bottom_right,_#ede9fe,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "screenshot",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="text-sm font-black uppercase tracking-[0.24em] text-emerald-700">
              Every correction can help the next learner
            </div>

            <h3 className="mx-auto mt-3 max-w-6xl text-3xl font-black leading-[1.06] text-gray-950 sm:text-4xl xl:text-[3.55rem]">
              A useful comment stays
              <br />
              next to the example.
            </h3>
          </div>

          <div className="relative mx-auto mt-8 w-full max-w-6xl">
            <div className="absolute inset-0 scale-[1.012] rounded-[2.6rem] bg-gradient-to-r from-emerald-200/30 via-blue-200/20 to-purple-200/25 blur-3xl" />

            <ZoomableImage
              src={asset("rated-enhanced.png")}
              alt="Community pronunciation review with comments and marked pronunciation issues"
              caption="Community pronunciation review"
              className="relative block"
            >
              <div className="overflow-hidden rounded-[2.5rem] border border-white/80 bg-white/92 p-3 shadow-[0_30px_95px_rgba(15,23,42,0.15)] ring-1 ring-gray-200/70 transition-all duration-300 group-hover:scale-[1.01]">
                <div className="overflow-hidden rounded-[1.8rem] border border-gray-100 bg-white">
                  <img
                    src={asset("rated-enhanced.png")}
                    alt="Community pronunciation review with comments and marked pronunciation issues"
                    className="mx-auto max-h-[395px] w-full object-contain contrast-[1.05] brightness-[1.02]"
                  />
                </div>
              </div>
            </ZoomableImage>
          </div>

          <div className="mx-auto mt-7 grid w-full max-w-6xl gap-4 md:grid-cols-3">
            {[
              "L1→L2 patterns",
              "Reusable corrections",
              "Better feedback model",
            ].map((label, index) => (
              <SoftCard
                key={index}
                className="rounded-[1.7rem] border border-gray-200/80 bg-white/88 px-5 py-5 text-center shadow-sm ring-1 ring-white/80"
              >
                <div className="flex min-h-[44px] items-center justify-center text-xl font-black leading-tight text-gray-950 xl:text-[1.45rem]">
                  {label}
                </div>
              </SoftCard>
            ))}
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_34%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "medium",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <div className="mx-auto max-w-5xl">
            <div className="text-sm font-black uppercase tracking-[0.24em] text-purple-700">
              Model under development
            </div>

            <h3 className="mx-auto mt-3 max-w-5xl text-4xl font-black leading-[1.04] text-gray-950 sm:text-5xl xl:text-[3.75rem]">
              Dialogue practice as
              <br />
              role rehearsal.
            </h3>

            <p className="mx-auto mt-5 max-w-4xl text-lg font-semibold leading-relaxed text-gray-600 sm:text-xl">
              Learners first build familiarity with the whole dialogue,
              then rehearse one role with gradually reduced support.
            </p>
          </div>

          <div className="mx-auto mt-9 grid w-full max-w-6xl items-stretch gap-4 md:grid-cols-[1fr_38px_1fr_38px_1fr_38px_1fr]">
            {[
              {
                label: "step 1",
                color: "blue",
                title: (
                  <>
                    Repeat
                    <br />
                    all lines
                  </>
                ),
                items: (
                  <>
                    listen
                    <br />
                    echo
                    <br />
                    shadow
                  </>
                ),
              },
              {
                label: "step 2",
                color: "purple",
                title: (
                  <>
                    Pick
                    <br />
                    a role
                  </>
                ),
                items: (
                  <>
                    Person A
                    <br />
                    Person B
                    <br />
                    same scene
                  </>
                ),
              },
              {
                label: "step 3",
                color: "pink",
                title: (
                  <>
                    Perform
                    <br />
                    the role
                  </>
                ),
                items: (
                  <>
                    partner line plays
                    <br />
                    you answer
                    <br />
                    cues if needed
                  </>
                ),
              },
              {
                label: "step 4",
                color: "emerald",
                title: (
                  <>
                    Get
                    <br />
                    a score
                  </>
                ),
                items: (
                  <>
                    pronunciation
                    <br />
                    timing
                    <br />
                    cue use
                  </>
                ),
              },
            ].map((step, index) => {
              const styles = {
                blue: {
                  border: "border-blue-100",
                  text: "text-blue-700",
                  bg: "bg-blue-50",
                },
                purple: {
                  border: "border-purple-100",
                  text: "text-purple-700",
                  bg: "bg-purple-50",
                },
                pink: {
                  border: "border-pink-100",
                  text: "text-pink-700",
                  bg: "bg-pink-50",
                },
                emerald: {
                  border: "border-emerald-100",
                  text: "text-emerald-700",
                  bg: "bg-emerald-50",
                },
              }[step.color];

              return (
                <>
                  <SoftCard
                    key={step.label}
                    className={`rounded-[2rem] border ${styles.border} bg-white/92 px-5 py-6 text-center shadow-sm ring-1 ring-white/80`}
                  >
                    <div className={`text-xs font-black uppercase tracking-[0.2em] ${styles.text}`}>
                      {step.label}
                    </div>

                    <div className="mt-4 flex min-h-[76px] items-center justify-center text-2xl font-black leading-tight text-gray-950 xl:text-[2rem]">
                      {step.title}
                    </div>

                    <div className={`mx-auto mt-5 rounded-2xl ${styles.bg} px-4 py-4 text-lg font-black leading-relaxed text-gray-700`}>
                      {step.items}
                    </div>
                  </SoftCard>

                  {index < 3 && (
                    <div className="hidden items-center justify-center md:flex">
                      <div className="text-4xl font-black text-purple-300">→</div>
                    </div>
                  )}
                </>
              );
            })}
          </div>

          <div className="mx-auto mt-7 w-full max-w-5xl rounded-[2rem] border border-gray-200/80 bg-white/84 px-6 py-5 shadow-sm ring-1 ring-white/80">
            <div className="text-sm font-black uppercase tracking-[0.2em] text-gray-500">
              cue ladder
            </div>

            <div className="mx-auto mt-4 grid gap-3 md:grid-cols-4">
              {[
                "first word",
                "reshuffled sentence",
                "voiced line",
                "voice + text",
              ].map((cue) => (
                <div
                  key={cue}
                  className="rounded-2xl bg-gray-50 px-4 py-3 text-lg font-black text-gray-900 ring-1 ring-gray-100"
                >
                  {cue}
                </div>
              ))}
            </div>

            <div className="mx-auto mt-4 max-w-4xl text-base font-bold leading-relaxed text-gray-600">
              Learners are rewarded not only for accuracy,
              but for needing less support.
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "medium",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <div className="mx-auto max-w-5xl">
            <div className="text-sm font-black uppercase tracking-[0.24em] text-emerald-700">
              Social challenge model
            </div>

            <h3 className="mx-auto mt-3 max-w-5xl text-4xl font-black leading-[1.04] text-gray-950 sm:text-5xl xl:text-[3.75rem]">
              Role rehearsal becomes
              <br />
              a peer challenge.
            </h3>

            <p className="mx-auto mt-5 max-w-4xl text-lg font-semibold leading-relaxed text-gray-600 sm:text-xl">
              A learner can challenge others to perform the same dialogue role.
              Both performances can be compared, reviewed, and improved.
            </p>
          </div>

          <div className="mx-auto mt-11 grid w-full max-w-6xl items-start gap-5 lg:grid-cols-[1fr_64px_1fr_64px_1fr]">
            <SoftCard className="rounded-[2.2rem] border border-blue-100 bg-white/92 px-6 py-8 text-center shadow-sm ring-1 ring-white/80">
              <div className="text-sm font-black uppercase tracking-[0.2em] text-blue-700">
                learner 1
              </div>

              <div className="mt-4 text-3xl font-black leading-tight text-gray-950">
                Performs
                <br />
                the role
              </div>

              <div className="mx-auto mt-6 rounded-2xl bg-blue-50 px-5 py-5 text-lg font-black leading-relaxed text-gray-700">
                performs first
                <br />
                sets benchmark
                <br />
                invites peer
              </div>
            </SoftCard>

            <div className="hidden lg:flex lg:items-center lg:justify-center lg:pt-28">
              <div className="text-5xl font-black text-purple-300">↔</div>
            </div>

            <div className="lg:pt-8">
              <SoftCard className="rounded-[2.2rem] border border-purple-200 bg-white/95 px-6 py-8 text-center shadow-md ring-1 ring-purple-100">
                <div className="text-sm font-black uppercase tracking-[0.2em] text-purple-700">
                  shared comparison
                </div>

                <div className="mt-4 text-3xl font-black leading-tight text-gray-950">
                  Score
                  <br />
                  + cues used
                </div>

                <div className="mx-auto mt-6 rounded-2xl bg-purple-50 px-5 py-5 text-lg font-black leading-relaxed text-gray-700">
                  pronunciation
                  <br />
                  cue penalty
                  <br />
                  replayable attempts
                </div>
              </SoftCard>
            </div>

            <div className="hidden lg:flex lg:items-center lg:justify-center lg:pt-28">
              <div className="text-5xl font-black text-purple-300">↔</div>
            </div>

            <div className="lg:pt-0 lg:pb-10">
              <SoftCard className="rounded-[2.2rem] border border-emerald-100 bg-white/92 px-6 py-8 text-center shadow-sm ring-1 ring-white/80">
                <div className="text-sm font-black uppercase tracking-[0.2em] text-emerald-700">
                  learner 2
                </div>

                <div className="mt-4 text-3xl font-black leading-tight text-gray-950">
                  Accepts
                  <br />
                  the challenge
                </div>

                <div className="mx-auto mt-6 rounded-2xl bg-emerald-50 px-5 py-5 text-lg font-black leading-relaxed text-gray-700">
                  same scene
                  <br />
                  same role
                  <br />
                  same rules
                </div>
              </SoftCard>
            </div>
          </div>

          <div className="mx-auto mt-10 max-w-5xl rounded-[2rem] bg-gradient-to-r from-emerald-100 via-blue-100 to-purple-100 px-8 py-6 shadow-sm ring-1 ring-white/80">
            <div className="text-2xl font-black leading-tight text-gray-950">
              Practice becomes something learners can share.
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_32%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "medium",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <div className="mx-auto max-w-5xl">
            <div className="text-xs font-black uppercase tracking-[0.24em] text-emerald-700 sm:text-sm">
              A possible institutional use
            </div>

            <h3 className="mx-auto mt-3 max-w-5xl text-3xl font-black leading-[1.05] text-gray-950 sm:text-4xl xl:text-[3.35rem]">
              Online rehearsal can make
              <br />
              offline speech practice more purposeful.
            </h3>

            <p className="mx-auto mt-5 max-w-4xl text-base font-semibold leading-relaxed text-gray-600 sm:text-lg xl:text-xl">
              Catchphraser can support language cafés, class sessions,
              speech clubs, and guided events by helping learners prepare beforehand.
            </p>
          </div>

          <div className="mx-auto mt-8 grid w-full max-w-6xl items-stretch gap-5 md:grid-cols-[1fr_68px_1fr]">
            <SoftCard className="rounded-[2.2rem] border border-purple-100 bg-white/90 px-7 py-7 text-left shadow-sm ring-1 ring-purple-100/70">
              <div className="text-xs font-black uppercase tracking-[0.22em] text-purple-700">
                before the gathering
              </div>

              <h4 className="mt-4 text-2xl font-black leading-tight text-gray-950 sm:text-3xl">
                Purposeful
                <br />
                preparation
              </h4>

              <div className="mt-6 space-y-3 text-lg font-black leading-snug text-gray-800 sm:text-xl">
                <div>practice dialogue roles</div>
                <div>repeat difficult lines</div>
                <div>receive feedback</div>
                <div>arrive more confident</div>
              </div>
            </SoftCard>

            <div className="hidden flex-col items-center justify-center md:flex">
              <div className="text-5xl font-black text-purple-300">→</div>

              <div className="mt-3 text-[10px] font-black uppercase leading-relaxed tracking-[0.24em] text-purple-400">
                prepared
                <br />
                participation
              </div>
            </div>

            <SoftCard className="rounded-[2.2rem] border border-emerald-100 bg-white/90 px-7 py-7 text-left shadow-sm ring-1 ring-emerald-100/70">
              <div className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">
                during the gathering
              </div>

              <h4 className="mt-4 text-2xl font-black leading-tight text-gray-950 sm:text-3xl">
                More useful
                <br />
                interaction
              </h4>

              <div className="mt-6 space-y-3 text-lg font-black leading-snug text-gray-800 sm:text-xl">
                <div>more time for speech</div>
                <div>less basic repair</div>
                <div>more contextual feedback</div>
                <div>stronger continuity</div>
              </div>
            </SoftCard>
          </div>

          <div className="mx-auto mt-8 max-w-5xl rounded-[2rem] bg-gradient-to-r from-emerald-100 via-blue-100 to-purple-100 px-8 py-5 shadow-sm ring-1 ring-white/80">
            <div className="text-xl font-black leading-tight text-gray-950 sm:text-2xl xl:text-[1.7rem]">
              Our core vision is to use online preparation
              <br className="hidden sm:block" />
              to support and encourage real-life speech activity.
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "",
      density: "medium",
      content: (
        <div className="mx-auto flex min-h-[620px] max-w-7xl flex-col justify-center px-4 py-5 text-center sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="text-xs font-black uppercase tracking-[0.26em] text-rose-700 sm:text-sm">
              The deeper shift
            </div>

            <h3 className="mx-auto mt-3 max-w-6xl text-4xl font-black leading-[1.04] tracking-tight text-gray-950 sm:text-5xl xl:text-[3.85rem]">
              Breaking the Pavlovian streak
              <br />
              in language-learning edtech.
            </h3>

            <p className="mx-auto mt-5 max-w-4xl text-lg font-bold leading-relaxed text-gray-600 sm:text-xl">
              Language-learning edtech has made practice more regular.
              <br className="hidden sm:block" />
              Our hope is to move further toward the nature of human speech.
            </p>
          </div>

          <div className="mx-auto mt-9 grid w-full max-w-6xl items-center gap-5 md:grid-cols-[1fr_110px_1fr]">
            <SoftCard className="rounded-[2.4rem] border border-rose-100 bg-white/92 px-8 py-8 text-center shadow-sm ring-1 ring-white/80">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-rose-100 to-orange-100 text-3xl shadow-sm">
                🔁
              </div>

              <div className="mt-5 text-[11px] font-black uppercase tracking-[0.26em] text-rose-700">
                habit layer
              </div>

              <h4 className="mt-4 text-3xl font-black leading-tight text-gray-950 xl:text-[2.15rem]">
                Habit-building
                <br />
                practice
              </h4>

              <div className="mx-auto mt-6 grid max-w-xs grid-cols-2 gap-2 text-base font-black text-gray-700">
                {[
                  "streaks",
                  "short exercises",
                  "sentence building",
                  "regular practice",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex min-h-[50px] items-center justify-center rounded-2xl bg-rose-50/80 px-4 py-3 ring-1 ring-rose-100"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </SoftCard>

            <div className="hidden flex-col items-center justify-center md:flex">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-4xl font-black text-purple-500 shadow-sm ring-1 ring-purple-100">
                →
              </div>

              <div className="mt-4 rounded-full bg-purple-50 px-4 py-3 text-[10px] font-black uppercase leading-relaxed tracking-[0.22em] text-purple-600 ring-1 ring-purple-100">
                toward
                <br />
                interaction
              </div>
            </div>

            <SoftCard className="rounded-[2.4rem] border border-emerald-100 bg-white/92 px-8 py-8 text-center shadow-sm ring-1 ring-white/80">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-100 to-blue-100 text-3xl shadow-sm">
                🤝
              </div>

              <div className="mt-5 text-[11px] font-black uppercase tracking-[0.26em] text-emerald-700">
                speech layer
              </div>

              <h4 className="mt-4 text-3xl font-black leading-tight text-gray-950 xl:text-[2.15rem]">
                Human speech
                <br />
                practice
              </h4>

              <div className="mx-auto mt-6 grid max-w-xs grid-cols-2 gap-2 text-base font-black text-gray-700">
                {["intention", "rehearsal", "feedback", "social use"].map(
                  (item) => (
                    <div
                      key={item}
                      className="flex min-h-[50px] items-center justify-center rounded-2xl bg-emerald-50/80 px-4 py-3 ring-1 ring-emerald-100"
                    >
                      {item}
                    </div>
                  )
                )}
              </div>
            </SoftCard>
          </div>

          <div className="mx-auto mt-9 max-w-5xl rounded-[2rem] bg-gradient-to-r from-rose-100 via-purple-100 to-emerald-100 px-8 py-6 shadow-sm ring-1 ring-white/80">
            <div className="text-2xl font-black leading-tight text-gray-950 sm:text-3xl">
              The goal is not to replace habit formation.
              <br />
              It is to connect it with social speech.
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#ffe4e6,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },
  ],
},
 pedagogy: {
  label: "Pedagogy",
  description: "learning theory",
  slides: [
    {
      title: "More Than Immersion",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Second-language acquisition
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Exposure is important, but progress often depends on interaction,
              feedback, and opportunities to modify one’s output.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Immersion alone is not always enough.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Intermediate learners often know enough to communicate, but not
            enough to speak with confidence, nuance, rhythm, humour, or social
            precision. They need structured situations where speech can be tried,
            adjusted, repeated, and made meaningful.
          </p>

          <div className="grid gap-4 md:grid-cols-4 xl:gap-5">
            {[
              ["Input", "varied language exposure"],
              ["Interaction", "negotiation of meaning"],
              ["Output", "attempts that can be modified"],
              ["Feedback", "noticing and repair"],
            ].map(([title, text]) => (
              <SoftCard
                key={title}
                className="bg-white/82 text-center shadow-sm ring-1 ring-blue-100"
              >
                <div className="text-lg font-black text-gray-950">
                  {title}
                </div>
                <div className="mt-3 text-sm font-semibold leading-relaxed text-gray-600">
                  {text}
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="The goal is not exposure alone." compact>
            The goal is meaningful speech that can be attempted, noticed,
            repaired, and used again.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Intermediate Learners Need Expressive Range",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Communicative competence
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Language mastery is not only grammatical or semantic. It is also
              social, pragmatic, prosodic, and affective.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            After the course, learners need more than correctness.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            They need practice with tone, timing, emotional colour, register,
            irony, hesitation, turn-taking, and social response — the parts of
            speech that make communication feel human.
          </p>

          <div className="grid gap-4 md:grid-cols-3 xl:gap-5">
            {[
              ["Prosody", "stress, rhythm, intonation, timing"],
              ["Pragmatics", "register, politeness, turn-taking"],
              ["Affect", "emotion, humour, hesitation, stance"],
            ].map(([title, text]) => (
              <SoftCard
                key={title}
                className="bg-white/82 text-center shadow-sm ring-1 ring-purple-100"
              >
                <div className="text-xl font-black text-gray-950">
                  {title}
                </div>
                <div className="mt-3 text-sm font-semibold leading-relaxed text-gray-600">
                  {text}
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="The plateau is not only linguistic." tone="purple" compact>
            It is also social, expressive, and performative.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Why Rehearsal Matters",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-amber-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-amber-700">
              Drama-based language learning
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Drama creates structured, repeatable, emotionally varied, socially
              meaningful speaking situations.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Theater is useful because it creates roles.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            A role gives the learner a reason to speak differently: with another
            intention, emotion, relation, rhythm, or level of formality. This
            helps learners move beyond their fixed personal routines.
          </p>

          <ComparisonTable
            leftTitle="Ordinary conversation"
            rightTitle="Rehearsed role"
            rows={[
              ["repeated personal topics", "varied communicative situations"],
              ["same speech habits", "new tone, rhythm, and register"],
              ["low preparation", "practice between meetings"],
              ["unclear progression", "shared goal and culmination"],
            ]}
          />

          <BigStatement title="Rehearsal makes repetition purposeful." tone="amber" compact>
            Learners are not just repeating; they are preparing for a shared
            communicative event.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#fffbeb)]",
    },

    {
      title: "Scripts Reduce Cognitive Load",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Scaffolding
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              When the language material is structured, learners can focus more
              deeply on delivery, timing, pronunciation, and response.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Given words create space for better speech.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Scripts reduce the pressure to invent content. This allows learners
            to concentrate on how the utterance is delivered: rhythm, stress,
            intonation, emotional force, and interactional timing.
          </p>

          <ComparisonTable
            leftTitle="Free conversation"
            rightTitle="Script-based dialogue"
            rows={[
              ["what should I say?", "how should I say it?"],
              ["topic search", "delivery focus"],
              ["grammar pressure", "voice and timing"],
              ["conversation anxiety", "prepared interaction"],
            ]}
          />

          <BigStatement title="The script is not the goal." tone="purple" compact>
            It is a scaffold for socially usable speech.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Why Group Practice Matters",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Peer interaction
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Learning can happen not only through teacher correction, but also
              through participation, comparison, imitation, and peer feedback.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            A group changes the social dynamics of correction.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            One-to-one tutoring is valuable, but it can become asymmetrical:
            one person evaluates, the other is evaluated. In a group, learners
            see that others struggle too, notice similar mistakes, and learn by
            observing repair in others before recognizing it in themselves.
          </p>

          <div className="grid gap-4 md:grid-cols-3 xl:gap-5">
            {[
              ["Shared difficulty", "learners see that struggle is normal"],
              ["Peer noticing", "mistakes in others become easier to identify"],
              ["Collective repair", "feedback becomes part of group practice"],
            ].map(([title, text]) => (
              <SoftCard
                key={title}
                className="bg-white/82 text-center shadow-sm ring-1 ring-emerald-100"
              >
                <div className="text-xl font-black text-gray-950">
                  {title}
                </div>
                <div className="mt-3 text-sm font-semibold leading-relaxed text-gray-600">
                  {text}
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="Correction becomes less confrontational." tone="green" compact>
            Learners are not only corrected by an authority; they move through
            the same process together.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
    },

    {
      title: "From Language Café To Rehearsal Community",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Social learning design
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Language cafés provide access and community. Rehearsal adds
              preparation, continuity, progression, and shared purpose.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            The missing ingredient is continuity.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Drop-in conversation is valuable, but learners may return to the
            same topics and habits. Rehearsal creates commitment: participants
            prepare between meetings and move toward something shared.
          </p>

          <ComparisonTable
            leftTitle="Language café"
            rightTitle="Rehearsal community"
            rows={[
              ["low-pressure access", "structured preparation"],
              ["open conversation", "roles and tasks"],
              ["limited continuity", "progress across sessions"],
              ["community contact", "shared communicative goal"],
            ]}
          />

          <BigStatement title="Community becomes more powerful with structure." compact>
            The platform can support the work before, during, and after social
            practice.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "The Platform Hypothesis",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-gray-200 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-gray-600">
              Research-informed, not yet product-proven
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              The exact platform still needs validation, but its components come
              from well-established traditions in second-language learning.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Catchphraser combines several supported mechanisms.
          </h3>

          <div className="mx-auto grid max-w-6xl gap-4 text-left md:grid-cols-2 xl:gap-5">
            {[
              ["Interaction", "language is negotiated and modified"],
              ["Feedback", "learners notice and repair problems"],
              ["Task repetition", "practice becomes more fluent over time"],
              ["Role-play", "speech gains emotional and social variation"],
              ["Peer learning", "learners improve by reviewing others"],
              ["Shared goals", "motivation comes from participation, not only streaks"],
            ].map(([title, text]) => (
              <SoftCard
                key={title}
                className="bg-white/82 shadow-sm ring-1 ring-gray-100"
              >
                <div className="text-lg font-black text-gray-950">
                  {title}
                </div>
                <div className="mt-2 text-sm font-semibold leading-relaxed text-gray-600">
                  {text}
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="The claim is modest but strong." tone="gray" compact>
            Not that the whole platform is already proven — but that it combines
            pedagogically meaningful mechanisms in one social practice system.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#f8fafc)]",
    },
  ],
},

    technology: {
      label: "Technology",
      description: "product workflows",
      slides: [
        {
          title: "AI-Supported Pronunciation Training",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl px-3 py-0 sm:px-4">
              <div className="grid items-center gap-5 xl:grid-cols-[1.42fr_0.58fr] xl:gap-7">
                <div className="space-y-4 text-center xl:space-y-5">
                  <h3 className="mx-auto max-w-4xl text-3xl font-black leading-tight text-gray-950 xl:text-4xl">
                    AI feedback gives learners a private preparation loop.
                  </h3>

                  <div className="mx-auto max-w-4xl rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.11)]">
                    <img
                      src={asset("kattenspiserikkefisk.png")}
                      alt="AI pronunciation feedback interface"
                      className="w-full rounded-[1.2rem] border border-gray-100 object-contain"
                    />

                    <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
                      AI-supported pronunciation practice with phonetic feedback
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
                  <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-left shadow-none ring-1 ring-blue-100">
                    <h4 className="mb-3 text-xl font-black text-gray-950">
                      Private preparation
                    </h4>

                    <CleanList
                      accent="blue"
                      items={[
                        "safe repetition",
                        "immediate feedback",
                        "pronunciation correction",
                        "unlimited attempts",
                      ]}
                    />
                  </SoftCard>

                  <SoftCard className="bg-gradient-to-br from-purple-50 to-white text-left shadow-none ring-1 ring-purple-100">
                    <h4 className="mb-3 text-xl font-black text-gray-950">
                      Human activation
                    </h4>

                    <CleanList
                      accent="purple"
                      items={[
                        "real listener response",
                        "meaningful explanation",
                        "social interpretation",
                        "collaborative attention",
                      ]}
                    />
                  </SoftCard>
                </div>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
        },

        {
          title: "Community Pronunciation Review",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl space-y-4 px-3 py-0 sm:px-4 xl:space-y-5">
              <div className="text-center">
                <SectionLabel>Product Workflow</SectionLabel>
                <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                  Learners can ask the community to review pronunciation.
                </h3>
              </div>

              <div className="grid items-center gap-5 lg:grid-cols-[1.35fr_0.65fr] xl:gap-7">
                <ScreenshotFrame
                  src={asset("rateme-enhanced.png")}
                  alt="Rate Me pronunciation review interface"
                  caption="Learner request for structured pronunciation feedback"
                  imgClassName="max-h-[430px] object-contain bg-gray-50 xl:max-h-[500px]"
                />

                <SoftCard className="bg-blue-50/70 ring-1 ring-blue-100">
                  <h4 className="mb-4 text-xl font-black text-gray-950 xl:text-2xl">
                    Reviewers can
                  </h4>
                  <CleanList
                    accent="blue"
                    items={[
                      "replay the learner attempt",
                      "compare it with narrator audio",
                      "inspect rhythm and melody",
                      "mark unclear words or sounds",
                      "leave concrete suggestions",
                    ]}
                  />
                </SoftCard>
              </div>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

        {
          title: "Collaborative Repair",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-6xl space-y-4 px-3 py-0 text-center sm:px-4">
              <div className="mx-auto max-w-4xl rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <img
                  src={asset("rated-enhanced.png")}
                  alt="Collaborative pronunciation review"
                  className="max-h-[300px] w-full rounded-[1.2rem] border border-gray-100 object-contain sm:max-h-[340px] xl:max-h-[365px]"
                />

                <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
                  Pronunciation requests with ratings, marked segments, and discussion
                </div>
              </div>

              <div className="mx-auto max-w-4xl rounded-3xl border border-white/70 bg-gradient-to-r from-blue-50 via-white to-purple-50 px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:px-6">
                <p className="text-base font-semibold leading-relaxed text-gray-700 sm:text-lg">
                  Learners improve not only by speaking, but by listening,
                  evaluating, explaining, and repairing together.
                </p>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#f0fdf4,#faf5ff)]",
        },

        {
          title: "L1-Sensitive Feedback",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                The same mistake often has different causes.
              </h3>

              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Feedback becomes more useful when it knows what language background
                the learner is coming from.
              </p>

              <ComparisonTable
                leftTitle="Observed patterns"
                rightTitle="Adaptive guidance"
                leftColor="green"
                rows={[
                  ["learners mark difficult words", "explanations fit the learner’s L1"],
                  ["reviewers notice recurring errors", "practice targets the likely cause"],
                  ["similar learners struggle in similar places", "examples become more precise"],
                  ["corrections accumulate over time", "feedback becomes reusable"],
                ]}
              />

              <BigStatement
                title="This creates accumulated pronunciation knowledge."
                tone="green"
                compact
              >
                A Russian speaker and a French speaker learning Norwegian may need
                different explanations, examples, and repair paths.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
        },

        {
          title: "Prosody Visualization",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl space-y-4 px-3 py-0 text-center sm:px-4">
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Seeing rhythm, stress, and intonation.
              </h3>

              <ScreenshotFrame
                src={asset("muchwarmerthere.png")}
                alt="Pitch contour visualization showing intonation groups and pitch variation across phrase boundaries"
                className="mx-auto max-w-6xl"
                imgClassName="max-h-[270px] w-full object-contain bg-gray-50 sm:max-h-[310px] xl:max-h-[335px]"
              />

              <div className="grid gap-4 md:grid-cols-3">
                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-blue-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Intonation groups
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    Pitch develops across whole phrases, not only isolated words.
                  </p>
                </SoftCard>

                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-purple-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-purple-600">
                    Word boundaries
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    The contour travels through connected speech and reveals phrasing.
                  </p>
                </SoftCard>

                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-pink-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-pink-600">
                    Stress perception
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    Timing, prominence, and emphasis become easier to notice.
                  </p>
                </SoftCard>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
        },
      ],
    },

 community: {
  label: "Community",
  description: "social speech practice",
  slides: [
    {
      title: "Speaking Develops Socially",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-200 bg-gradient-to-r from-purple-100 via-white to-pink-100 px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-lg italic leading-relaxed text-gray-800 sm:text-xl">
              “Human learning presupposes a specific social nature.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Speaking is not only language production.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            People learn to speak through interaction:
            listening, responding, repairing misunderstandings,
            adjusting timing, and gradually becoming comfortable
            participating with others.
          </p>

          <ComparisonTable
            leftTitle="Private AI practice"
            rightTitle="Social speech rehearsal"
            leftColor="blue"
            rightColor="purple"
            rows={[
              ["individual repetition", "interaction with listeners"],
              ["private correction", "shared repair and feedback"],
              ["simulated conversation", "social coordination"],
              ["isolated learning loop", "visible group progression"],
            ]}
          />

          <BigStatement
            title="AI can simulate conversation, but speech is still fundamentally social."
            tone="purple"
            compact
          >
            Catchphraser uses scripts, peer review, rehearsal,
            and guided interaction to help learners move from
            isolated practice toward real participation with other people.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Peers Make Progress Visible",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “What a learner can do in cooperation today,
              they can do independently tomorrow.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners improve by hearing others improve.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Native-speaker models show the target,
            but peers show the process:
            hesitation, repair, adaptation, and gradual fluency.
          </p>

          <ComparisonTable
            leftTitle="Models"
            rightTitle="Peers"
            rows={[
              ["show the target", "show the path"],
              ["ideal pronunciation", "visible gradual improvement"],
              ["reference", "participation"],
              ["polished delivery", "realistic variability"],
            ]}
          />

          <BigStatement title="Speech confidence grows socially." tone="purple" compact>
            Learners become less inhibited when they hear people like themselves
            struggling, repairing, and gradually improving together.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Shared Practice Reduces Pressure",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Mikhail Bakhtin
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “To be means to communicate.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners speak more when they feel safe enough to continue.
          </h3>

          <ComparisonTable
            leftTitle="High-pressure speech"
            rightTitle="Shared rehearsal"
            leftColor="amber"
            rightColor="green"
            rows={[
              ["one learner is evaluated", "everyone is visibly learning"],
              ["mistakes feel personal", "mistakes become normal"],
              ["perfect models create pressure", "similar learners reduce shame"],
              ["correction interrupts confidence", "repair becomes collaborative"],
            ]}
          />

          <BigStatement title="The goal is usable participation." tone="green" compact>
            Learners gradually become more comfortable speaking because
            speech becomes shared work rather than isolated performance.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
    },

    {
      title: "Dialogue Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

        <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
  <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
    Mikhail Bakhtin
  </div>

  <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
    “The utterance is filled with dialogic overtones.”
  </div>
</div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Prepared dialogue turns speaking into coordination.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Instead of inventing every sentence from scratch,
            learners can focus on timing, pronunciation,
            response, listening, and interaction.
          </p>

          <Timeline
            steps={[
              ["🎧", "Prepare", "individual rehearsal", "listen, repeat, inspect prosody", "ring-blue-100"],
              ["💬", "Review", "peer correction", "mark unclear rhythm and sounds", "ring-purple-100"],
              ["👥", "Perform", "dialogue together", "response timing and coordination", "ring-pink-100"],
            ]}
          />

          <BigStatement title="The script is a scaffold for social speech." tone="purple" compact>
            Learners move gradually from guided rehearsal toward more natural interaction.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Guided Social Events",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-pink-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-pink-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “Through others we become ourselves.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Guided events create stronger social motivation.
          </h3>

          <div className="grid gap-5 md:grid-cols-3 xl:gap-6">
            <EmojiCard
              emoji="👥"
              title="Learners"
              text="prepare, review, rehearse"
              className="ring-1 ring-blue-100"
            />

            <EmojiCard
              emoji="🧑‍🏫"
              title="Guides"
              text="structure sessions and progression"
              className="ring-1 ring-purple-100"
            />

            <EmojiCard
              emoji="🎙️"
              title="Events"
              text="radio plays, workshops, staged readings"
              className="ring-1 ring-pink-100"
            />
          </div>

          <BigStatement title="Speech practice becomes a coordinated social activity." tone="purple" compact>
            The long-term direction is not only AI tutoring,
            but communities practicing speech together.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },
  ],
},


gamification: {
  label: "Gamification",
  description: "rehearsal & dialogue challenges",
  slides: [
    {
      title: "Speech As Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Central analogy
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              An actor learns a role by rehearsing lines together with others.
              Speech practice can work in a similar way.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners rehearse dialogue roles.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The system plays the opposite role while the learner practices responses,
            timing, pronunciation, and delivery inside a structured interaction.
          </p>

          <div className="grid gap-4 md:grid-cols-3 xl:gap-5">
            <SoftCard className="text-left ring-1 ring-blue-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                Round 1
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Learn the lines
              </h4>

              <CleanList
                accent="blue"
                items={[
                  "hear all dialogue lines",
                  "repeat own and opponent lines",
                  "practice pronunciation and rhythm",
                  "become familiar with the scene",
                ]}
              />
            </SoftCard>

            <SoftCard className="text-left ring-1 ring-purple-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-purple-700">
                Round 2
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Rehearse the scene
              </h4>

              <CleanList
                accent="purple"
                items={[
                  "only opponent lines are played",
                  "produce missing responses",
                  "practice timing and recall",
                  "stay inside interaction flow",
                ]}
              />
            </SoftCard>

            <SoftCard className="text-left ring-1 ring-pink-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-pink-700">
                Round 3
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Perform the role
              </h4>

              <CleanList
                accent="pink"
                items={[
                  "minimal or no support",
                  "score depends on hint usage",
                  "AI evaluates performance",
                  "pronunciation and timing assessment",
                ]}
              />
            </SoftCard>
          </div>

          <BigStatement
            title="The learner does not only memorize sentences."
            tone="purple"
            compact
          >
            The learner rehearses timing, response, pronunciation,
            and participation inside structured interaction.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Support And Progression",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Guided difficulty
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Support gradually disappears as the learner becomes more confident.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Hints function as temporary scaffolding.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The system can provide different levels of support while keeping
            the learner inside the interaction loop.
          </p>

          <ComparisonTable
            leftTitle="Hint type"
            rightTitle="Learning function"
            leftColor="blue"
            rightColor="purple"
            rows={[
              ["first-word cue", "small retrieval support"],
              ["shuffled words", "sentence reconstruction"],
              ["audio replay", "prosody and pronunciation support"],
              ["spoken line + text", "maximum scaffold"],
            ]}
          />

          <BigStatement
            title="More support gives lower scores."
            tone="green"
            compact
          >
            The scoring system rewards increasing independence,
            while still allowing learners to continue practicing safely.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Why The Game Loop Supports Learning",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Pedagogical logic
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              The game mechanics are derived from rehearsal, retrieval,
              scaffolding, and repeated exposure to useful language patterns.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            The interaction loop supports speech automaticity.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The learner responds to a spoken cue, not just a written prompt.
            This trains pronunciation, intonation, rhythm, and timing inside
            a realistic dialogue exchange.
          </p>

          <ComparisonTable
            leftTitle="Learning principle"
            rightTitle="Interactive implementation"
            leftColor="green"
            rightColor="purple"
            rows={[
              ["repeated language exposure", "hear dialogue patterns many times"],
              ["chunk and pattern learning", "practice complete useful responses"],
              ["retrieval practice", "produce missing lines from memory"],
              ["prosodic response", "answer spoken cues with rhythm and intonation"],
              ["gradual scaffolding removal", "reduce hints over time"],
            ]}
          />

          <BigStatement
            title="The score measures independence, delivery, and timing."
            tone="green"
            compact
          >
            Fewer hints, fewer retries, clearer pronunciation, and more natural intonation
            show movement from guided rehearsal toward increasingly automatic spoken participation.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "Challenge & Shared Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Social practice layer
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              When a learner is ready, the same scene can become a challenge,
              a shared recording, or preparation for offline rehearsal.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Rehearsal becomes social when learners perform the same scene.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Learners can challenge others on the same role, perform against
            another user’s recorded lines, and receive AI feedback with optional
            native-speaker review.
          </p>

          <ComparisonTable
            leftTitle="Social mechanic"
            rightTitle="Learning value"
            leftColor="purple"
            rightColor="green"
            rows={[
              ["challenge another learner", "same scene creates fair comparison"],
              ["use another user’s cue lines", "practice feels more alive"],
              ["AI scores pronunciation and timing", "feedback remains immediate"],
              ["native speaker can review", "human assessment can override AI"],
            ]}
          />

          <BigStatement
            title="The long-term goal is purposeful social rehearsal."
            tone="purple"
            compact
          >
            Online challenges can connect naturally to language cafés,
            reading groups, workshops, and offline dialogue practice.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },
  ],
},

    business: {
      label: "Business",
      description: "monetization and team",
      slides: [
        {
          title: "Monetization & Partnerships",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <ConceptMark emoji="💼" label="Business" sub="community-first model" />
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Monetization follows the learning model.
              </h3>
              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Catchphraser can become infrastructure for organized speech practice,
                guided rehearsal, narrated content, institutional speaking activities,
                and creator-led courses.
              </p>

              <div className="grid gap-4 md:grid-cols-4 xl:gap-5">
                <EmojiCard
                  emoji="🎧"
                  title="Publishers"
                  text="narrated content as interactive practice"
                  className="ring-1 ring-blue-100"
                />
                <EmojiCard
                  emoji="🎓"
                  title="Courses"
                  text="structured courses and revenue sharing"
                  className="ring-1 ring-amber-100"
                />
                <EmojiCard
                  emoji="🧑‍🏫"
                  title="Guided activities"
                  text="paid rooms, workshops, rehearsal groups"
                  className="ring-1 ring-purple-100"
                />
                <EmojiCard
                  emoji="🏛️"
                  title="Institutions"
                  text="university language cafés and cohorts"
                  className="ring-1 ring-emerald-100"
                />
              </div>

              <BigStatement title="The first offer: structured social practice and course infrastructure." tone="green" compact>
                Free peer exchange can bring users in. Guided rooms, institutional cohorts,
                publisher content, creator courses, and specialized workshops create monetization.
              </BigStatement>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

        {
          title: "Long-Term Advantage",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                The platform becomes harder to copy as the ecosystem grows.
              </h3>
              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Learners, institutions, publishers, and guided events create accumulated value:
                speech data, content, relationships, and community habits.
              </p>

              <ComparisonTable
                leftTitle="Accumulated knowledge"
                rightTitle="Content network"
                leftColor="green"
                rightColor="amber"
                rows={[
                  ["learner attempts create signal", "native-speaker audio creates quality"],
                  ["peer comments reveal patterns", "publishers get a new channel"],
                  ["L1-specific errors accumulate", "content becomes interactive practice"],
                  ["recommendations improve", "better material attracts learners"],
                ]}
              />

              <BigStatement title="A competitor can copy a feature, but not the accumulated ecosystem." tone="amber" compact>
                The long-term advantage is L1-specific pronunciation data,
                licensed practice material, institutional relationships,
                and a growing speech community.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#fffbeb)]",
        },

        {
          title: "Content & Course Platform",
          density: "dense",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4">
              <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Narrated content and courses become interactive speech practice.
              </h3>

              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-600 sm:text-lg">
                Audiobooks, dialogues, graded readers, pronunciation courses, and
                teacher-made materials can become rehearsal, shadowing, feedback, and
                collaborative speaking experiences.
              </p>

              <div className="grid gap-5 md:grid-cols-2">
                <SoftCard className="bg-gradient-to-br from-amber-50 to-white text-left ring-1 ring-amber-200">
                  <div className="mb-4 text-center">
                    <div className="mb-1 text-xs font-black uppercase tracking-[0.18em] text-amber-700">
                      Creator value
                    </div>

                    <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                      New distribution layer
                    </h4>
                  </div>

                  <NumberedList
                    accent="amber"
                    items={[
                      "turn narrated material into interactive speech training",
                      "subscription or course revenue",
                      "developer and teacher publishing portal",
                      "platform handles rehearsal and delivery",
                    ]}
                  />
                </SoftCard>

                <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-left ring-1 ring-blue-200">
                  <div className="mb-4 text-center">
                    <div className="mb-1 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                      Learner value
                    </div>

                    <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                      Structured speaking progression
                    </h4>
                  </div>

                  <NumberedList
                    accent="blue"
                    items={[
                      "high-quality speech models",
                      "practice with AI and human feedback",
                      "reading + listening + speaking integration",
                      "social rehearsal around shared material",
                    ]}
                  />
                </SoftCard>
              </div>

              <BigStatement title="This is not only content licensing." tone="amber" compact>
                The platform transforms narrated material and expert-made courses into
                reusable speech-practice infrastructure.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#fffdf5,#fffbeb)]",
        },

        {
          title: "The Catchphraser Team",
          density: "team",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <div className="text-center">
                <h2 className="mb-3 text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                  The Catchphraser Team
                </h2>
                <p className="mx-auto max-w-3xl text-base leading-relaxed text-gray-600 sm:text-lg xl:text-xl">
                  Researchers and language professionals building pronunciation training and rehearsal infrastructure.
                </p>
              </div>

              <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-3 xl:gap-6">
                {[
                  [
                    "it.jpg",
                    "Dr. Inna Tolskaya",
                    "CEO – Product & Linguistics",
                    "itolsk@gmail.com",
                    "Linguist, project manager, and software developer with a passion for innovation and e-learning.",
                    "from-blue-50 to-indigo-50 border-blue-200 text-blue-600",
                    "IT",
                  ],
                  [
                    "oh.jpg",
                    "Assoc. Prof. Olaf Husby",
                    "CCO – Teaching & Didactics",
                    "olafhusby@gmail.com",
                    "Phonetician, second-language educator, course developer, and Norwegian dialects evangelist.",
                    "from-purple-50 to-violet-50 border-purple-200 text-purple-600",
                    "OH",
                  ],
                  [
                    "ds.jpg",
                    "Dr. Dmitry Shcherbin",
                    "CTO/CPO – Tech & Research",
                    "dmitry.shcherbin@gmail.com",
                    "Product leader, software developer, data analyst, and researcher with an interest in practical applications.",
                    "from-cyan-50 to-teal-50 border-cyan-200 text-cyan-600",
                    "DS",
                  ],
                ].map(([img, name, role, email, bio, cardColor, initials]) => (
                  <div
                    key={name}
                    className={`rounded-3xl border bg-gradient-to-br ${cardColor} p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] xl:p-5`}
                  >
                    <div className="text-center">
                      <div className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-gray-100 shadow-xl xl:h-24 xl:w-24">
                        <div className="absolute inset-0 flex items-center justify-center text-3xl font-black text-gray-500">
                          {initials}
                        </div>
                        <img
                          src={asset(img)}
                          alt={name}
                          className="relative z-10 h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      </div>

                      <h3 className="mb-2 text-lg font-black text-gray-950 xl:text-xl">
                        {name}
                      </h3>
                      <p className="mb-2 text-sm font-bold">{role}</p>
                      <a href={`mailto:${email}`} className="break-all text-xs hover:underline">
                        {email}
                      </a>
                    </div>

                    <div className="mt-4 rounded-2xl border border-white/60 bg-white/85 p-4 shadow-sm backdrop-blur-sm">
                      <p className="text-sm leading-relaxed text-gray-700">{bio}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <p className="mb-1 text-sm text-gray-700 sm:text-base">
                  Interested in collaboration, testing, or research partnerships?
                </p>
                <a href="mailto:info@catchphraser.com" className="text-base font-bold text-blue-600 hover:underline">
                  info@catchphraser.com
                </a>
              </div>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#ecfeff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },
      ],
    },
  };
};